import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import moment from 'moment-timezone';
import { parseTimeBlurInput } from 'utils/dateTimeUtils';

/**
 * PrimeReact Calendar time spinners add/subtract `stepMinute` from the current minutes.
 * From 10:18 with step 30 that yields 10:48 / 9:48 instead of 10:30 / 10:00.
 * When the previous wall time was not on the step grid and the change is exactly ±step,
 * treat it as a spinner nudge and snap to the next / previous step boundary instead.
 * Typed times (deltas other than ±step) are left unchanged.
 */
function minutesSinceMidnight(d) {
  return d.getHours() * 60 + d.getMinutes();
}

function normalizeMinuteDelta(delta) {
  if (delta > 720) return delta - 1440;
  if (delta < -720) return delta + 1440;
  return delta;
}

function snapTimeOnlySpinnerPick(prev, next, step) {
  if (!prev || !next || !step || step <= 1) return next;

  const prevM = minutesSinceMidnight(prev);
  const nextM = minutesSinceMidnight(next);
  const delta = normalizeMinuteDelta(nextM - prevM);

  if (prevM % step === 0) return next;

  const isSpinnerStep = delta === step || delta === -step;
  if (!isSpinnerStep) return next;

  let snappedM = delta > 0 ? Math.ceil(prevM / step) * step : Math.floor(prevM / step) * step;
  while (snappedM < 0) snappedM += 1440;
  while (snappedM >= 1440) snappedM -= 1440;

  const h = Math.floor(snappedM / 60);
  const min = snappedM % 60;
  return new Date(next.getFullYear(), next.getMonth(), next.getDate(), h, min, next.getSeconds(), next.getMilliseconds());
}

/** Nearest :00 / :30 (or other step) on the same calendar day for the browser-local wall time used by the Calendar. */
function snapLocalDisplayDateToNearestMinuteStep(date, step) {
  if (!date || !step || step <= 1) return date;
  const total = minutesSinceMidnight(date);
  if (total % step === 0) return date;
  let snapped = Math.round(total / step) * step;
  const maxSnapped = 24 * 60 - step;
  snapped = Math.max(0, Math.min(maxSnapped, snapped));
  const h = Math.floor(snapped / 60);
  const min = snapped % 60;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, min, 0, 0);
}

const scheduleMicrotask =
  typeof queueMicrotask === 'function'
    ? queueMicrotask
    : (fn) => {
        Promise.resolve().then(fn);
      };

/**
 * Match PrimeReact Calendar#formatTime so the visible input matches the overlay
 * (see componentDidUpdate: updateInputfield is skipped when overlay is open and viewStateChanged).
 */
function formatTimeOnlyInputLikePrimeReact(date, hourFormat, showSeconds, showMillisec) {
  if (!date) return '';
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const milliseconds = date.getMilliseconds();

  if (hourFormat === '12' && hours > 11 && hours !== 12) {
    hours -= 12;
  }

  let output = '';
  if (hourFormat === '12') {
    output += hours === 0 ? 12 : hours < 10 ? `0${hours}` : hours;
  } else {
    output += hours < 10 ? `0${hours}` : hours;
  }
  output += ':';
  output += minutes < 10 ? `0${minutes}` : minutes;
  if (showSeconds) {
    output += ':';
    output += seconds < 10 ? `0${seconds}` : seconds;
  }
  if (showMillisec) {
    output += '.';
    output += milliseconds < 100 ? (milliseconds < 10 ? `00${milliseconds}` : `0${milliseconds}`) : milliseconds;
  }
  if (hourFormat === '12') {
    output += date.getHours() > 11 ? ' PM' : ' AM';
  }
  return output;
}

const TimezoneDatePickerWithTime = ({
  value,
  onChange,
  timezone,
  timeOnly = false,
  stepMinute,
  onBlur,
  id,
  name,
  inputRef: inputRefProp,
  hourFormat = '12',
  showSeconds = false,
  showMillisec = false,
  onHide: onHideProp,
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState(null);
  const displayValueRef = useRef(null);
  displayValueRef.current = displayValue;
  const localInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const inputRefPropRef = useRef(inputRefProp);
  inputRefPropRef.current = inputRefProp;

  const mergedInputRef = useCallback((el) => {
    localInputRef.current = el;
    const ir = inputRefPropRef.current;
    if (ir) {
      if (typeof ir === 'function') {
        ir(el);
      } else {
        ir.current = el;
      }
    }
  }, []);

  const tz = timezone || moment.tz.guess();

  /**
   * Convert TZ date → browser-local Date for Calendar
   */
  const toDisplay = (date) => {
    if (!date) return null;

    // Parse the date in the target timezone
    const m = moment.tz(date, tz);

    if (timeOnly) {
      const today = new Date();
      const h = m.hours();
      const min = m.minutes();
      const sec = m.seconds();
      const ms = m.milliseconds();
      // Do not snap stored wall time to stepMinute here: that rounds display (e.g. 12:15 → 12:30)
      // and breaks typed/custom times. stepMinute snapping stays in handleChange for overlay picks.
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, min, sec, ms);
    }

    // IMPORTANT: create browser-local Date using wall-clock values
    return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond());
  };

  /**
   * Convert Calendar-picked Date → TZ Date
   */
  const fromDisplay = (date) => {
    if (!date) return null;

    if (timeOnly) {
      // For timeOnly: interpret the picked time in the target timezone
      // Use today's date in the target timezone as reference
      const todayInTz = moment.tz(tz);
      const result = moment.tz(
        {
          year: todayInTz.year(),
          month: todayInTz.month(),
          date: todayInTz.date(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
          millisecond: date.getMilliseconds(),
        },
        tz
      );

      return result.toDate();
    }

    // Extract wall-clock from browser date
    const parts = {
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: date.getMilliseconds(),
    };

    return moment.tz(parts, tz).toDate();
  };

  // Sync incoming value → Calendar
  useEffect(() => {
    if (!value) {
      setDisplayValue(null);
      return;
    }

    if (Array.isArray(value)) {
      setDisplayValue(value.map(toDisplay));
    } else {
      setDisplayValue(toDisplay(value));
    }
  }, [value, tz, timeOnly, stepMinute]);

  const commitParsedWallTimeFromRaw = (raw, blurEvent) => {
    if (!timeOnly || raw == null) return;
    const str = String(raw).trim();
    if (!str) return;

    const parsed = parseTimeBlurInput(str);
    if (!parsed) return;

    if (value && !Array.isArray(value)) {
      const current = moment.tz(value, tz);
      if (current.isValid() && current.hour() === parsed.hour && current.minute() === parsed.minute) {
        return;
      }
    }

    scheduleMicrotask(() => {
      const today = new Date();
      const displayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), parsed.hour, parsed.minute, 0, 0);
      const convertedValue = fromDisplay(displayDate);
      onChangeRef.current({
        originalEvent: blurEvent,
        value: convertedValue,
        target: {
          name: name || '',
          id: id || '',
          value: convertedValue,
        },
      });

      const syncInputValue = () => {
        const el = localInputRef.current;
        if (!el) return;
        el.value = formatTimeOnlyInputLikePrimeReact(displayDate, hourFormat, showSeconds, showMillisec);
      };
      setTimeout(syncInputValue, 0);
      setTimeout(syncInputValue, 50);
    });
  };

  const handleChange = (e) => {
    let picked = e.value;

    if (!picked) {
      onChange({ ...e, value: null, target: { ...e.target, value: null } });
      return;
    }

    if (timeOnly && stepMinute) {
      const prev = displayValueRef.current;
      if (Array.isArray(picked) && Array.isArray(prev)) {
        picked = picked.map((p, i) => {
          const a = prev[i] ? snapTimeOnlySpinnerPick(prev[i], p, stepMinute) : p;
          return snapLocalDisplayDateToNearestMinuteStep(a, stepMinute);
        });
      } else if (!Array.isArray(picked) && prev && !Array.isArray(prev)) {
        picked = snapTimeOnlySpinnerPick(prev, picked, stepMinute);
        picked = snapLocalDisplayDateToNearestMinuteStep(picked, stepMinute);
      } else if (!Array.isArray(picked)) {
        picked = snapLocalDisplayDateToNearestMinuteStep(picked, stepMinute);
      }
    }

    let convertedValue;
    if (Array.isArray(picked)) {
      convertedValue = picked.map(fromDisplay);
    } else {
      convertedValue = fromDisplay(picked);
    }

    onChange({
      ...e,
      value: convertedValue,
      target: { ...e.target, value: convertedValue },
    });
  };

  const handleBlur = (e) => {
    onBlur?.(e);

    const fromEvent = e?.target?.value;
    const raw =
      fromEvent != null && String(fromEvent).trim() !== ''
        ? String(fromEvent)
        : localInputRef.current?.value != null
        ? String(localInputRef.current.value)
        : '';

    commitParsedWallTimeFromRaw(raw, e);
  };

  const handleHide = () => {
    onHideProp?.();
    // After the panel closes, Prime may leave the text field out of sync with AM/PM; re-read the input.
    setTimeout(() => {
      const raw = localInputRef.current?.value;
      if (raw != null && String(raw).trim() !== '') {
        commitParsedWallTimeFromRaw(String(raw), null);
      }
    }, 0);
  };

  return (
    <Calendar
      {...rest}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onHide={handleHide}
      timeOnly={timeOnly}
      stepMinute={stepMinute}
      hourFormat={hourFormat}
      showSeconds={showSeconds}
      showMillisec={showMillisec}
      inputRef={mergedInputRef}
      id={id}
      name={name}
    />
  );
};

export default TimezoneDatePickerWithTime;
